import type { SerializedStyles, Theme } from "@emotion/react";
import { faCheck, faMinus } from "@fortawesome/pro-regular-svg-icons";
import type { HTMLProps, ReactNode } from "react";
import { useEffect, useRef } from "react";

import { FontAwesomeIcon } from "@shared/react-fontawesome";

import * as styles from "./DeprecatedCheckbox.styles";

interface Props extends HTMLProps<HTMLInputElement> {
  /**
   * Adds custom styles to the label text styling
   */
  labelStyle?: SerializedStyles[] | SerializedStyles | ((theme: Theme) => SerializedStyles);
  /**
   * Sets the checkbox wrapper to be fullwidth
   *
   * @default false
   */
  isFullWidth?: boolean;
  /**
   * Sets the checkbox to be indeterminate (an uncertain state where some options are checked, but not all)
   *
   * @default false
   */
  indeterminate?: boolean;
  /**
   * Content to render alongside the checkbox
   *
   * @default undefined
   */
  children?: ReactNode;
}

const DeprecatedCheckbox = ({
  children,
  disabled,
  id,
  isFullWidth = false,
  labelStyle,
  indeterminate = false,
  ...rest
}: Props) => {
  const checkboxRef = useRef<HTMLInputElement>();

  useEffect(() => {
    checkboxRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const wrapperStyles = () => [
    styles.checkbox.base,
    isFullWidth && styles.checkbox.isFullWidth,
    disabled && styles.checkbox.disabled,
    labelStyle,
  ];

  const iconContainerStyles = () => [
    styles.iconContainer.base,
    disabled && styles.iconContainer.disabled,
  ];

  const checkboxContentStyles = () => [
    styles.checkboxContent.base,
    disabled && styles.checkboxContent.disabled,
  ];

  const iconStyles = () => [styles.icon.base, disabled && styles.icon.disabled];

  return (
    <label htmlFor={id} css={wrapperStyles()} aria-disabled={disabled} data-testid="checkbox-label">
      <input
        id={id}
        css={styles.checkboxInput}
        type="checkbox"
        disabled={disabled}
        ref={checkboxRef}
        aria-checked={indeterminate ? "mixed" : rest.checked}
        {...rest}
      />
      <div css={iconContainerStyles()}>
        <FontAwesomeIcon css={iconStyles()} icon={indeterminate ? faMinus : faCheck} />
      </div>
      {!!children && <div css={checkboxContentStyles()}>{children}</div>}
    </label>
  );
};

export { DeprecatedCheckbox };
